import React, { useEffect, useState } from "react";
import DashboardCard from "../../Components/Card/DashboardCard";
import { useSelector } from "react-redux";
import {
  CircularProgress,
  Grid,
  Box,
  Container,
  Stack,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from "@mui/material";
import configServ from "../../services/config";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useNavigate } from "react-router-dom";
import AttendanceChart from "../../Components/Chart/AttendanceChart";
import PunchLogsDashboard from "../../Components/Chart/PunchLogsDashboard";
import Dashboards from "../../HR_Parts/Dashboards";
import AttendanceGraph from "../../HR_Parts/AttendanceGraph";

// FUNCTION
function Dashboard() {
  //#region code
  const { user_id, company_id, is_super, is_admin } = useSelector(
    (state) => state.GlobalVariables
  );
  const navigate = useNavigate();

  const [count, setCount] = useState({});
  const [sampleData, setSampleData] = useState(null);
  const [currentWorkingEmployees, setCurrentWorkingEmployees] = useState(null);

  // States for filters & dropdown lists
  const [departments, setDepartments] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedSite, setSelectedSite] = useState("");

  const fetchDashboardCount = async () => {
    try {
      if (user_id === null) {
        navigate("/");
      }
      const result = await configServ.getDashboardCount({ user_id, company_id });
      setCount(result);
    } catch (e) {
      console.log(e);
    }
  };

  // Fetch Departments
  const fetchDepartments = async () => {
    try {
      if (company_id) {
        const deptRes = await configServ.getAllDepartments(company_id);
        if (deptRes?.data) {
          setDepartments(deptRes.data);
        } else if (Array.isArray(deptRes)) {
          setDepartments(deptRes);
        }
      }
    } catch (e) {
      console.error("Error fetching departments:", e);
    }
  };

  // Fetch Sites (Standard type)
  const fetchSiteList = async () => {
    try {
      const result = await configServ.get_customer({ company_id });
      const filteredSites = result?.filter((item) => item.type === "Standard");
      setSiteList(filteredSites || []);
    } catch (e) {
      console.error("Error fetching site list:", e);
    }
  };

  const fetchAttendanceChartData = async () => {
    try {
      const payload = {
        user_id,
        company_id,
        filter: "last_7_days",
        ...(selectedDepartment && { department_id: selectedDepartment }),
        ...(selectedSite && { site_id: selectedSite }),
      };
      const result = await configServ.getAttendanceChart(payload);
      setSampleData(result);
    } catch (e) {
      console.log(e);
    }
  };

  const fetchCurrentWorkingEmployeesData = async () => {
    try {
      const payload = {
        user_id,
        company_id,
        filter: "last_7_days",
        ...(selectedDepartment && { department_id: selectedDepartment }),
        ...(selectedSite && { site_id: selectedSite }),
      };
      const result = await configServ.currentWorkingEmployees(payload);
      setCurrentWorkingEmployees(result);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    fetchDashboardCount();
    fetchDepartments();
    fetchSiteList();
  }, []);

  // Selected Department ya Site change hone par chart data auto-update hoga
  useEffect(() => {
    fetchAttendanceChartData();
    fetchCurrentWorkingEmployeesData();
  }, [selectedDepartment, selectedSite]);

  const cardData = [
    {
      title: "SITES",
      number: count.subscription,
      icon: <PeopleIcon sx={{ color: "#6270e4", fontSize: 26 }} />,
      link: "/account-master-entry/customers",
      state: {
        type: "Standard",
      },
    },
  ];
  //#endregion

  const todayLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <>
      <Box sx={{ bgcolor: "#f4f5f9", width: "100%", minHeight: "100vh" }}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Page header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#6270e4",
                }}
              >
                <DashboardIcon sx={{ color: "#fff", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color="#1a1f36" lineHeight={1.2}>
                  Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Overview of your organization's activity
                </Typography>
              </Box>
            </Stack>

            <Chip
              label={todayLabel}
              variant="outlined"
              sx={{
                bgcolor: "#fff",
                borderColor: "#e0e3ec",
                fontWeight: 500,
                color: "#4a4f66",
              }}
            />
          </Stack>

          {/* Summary cards */}
          <Grid
            container
            alignItems="stretch"
            justifyContent={{ xs: "center", sm: "flex-start" }}
            spacing={2}
            sx={{
              backgroundColor: "#6270e4",
              width: "100%",
              m: 0,
              p: { xs: 1.5, sm: 2 },
              borderRadius: "12px",
              mb: 4,
              py: { xs: 2, sm: 3 },
            }}
          >
            {cardData.map((item, index) => (
              <Grid key={index} size={{ xs: 6, sm: 6, md: 4, lg: 3 }}>
                <DashboardCard index={index} data={item} onClick={item.onClick} />
              </Grid>
            ))}
          </Grid>

          {/* Filters Bar */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: "12px",
              border: "1px solid #e0e3ec",
              bgcolor: "#ffffff",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <FilterAltIcon sx={{ color: "#6270e4" }} />
                  <Typography variant="subtitle1" fontWeight={600} color="#1a1f36">
                    Filter Analytics
                  </Typography>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={4} md={4.5}>
                <FormControl fullWidth size="small">
                  <InputLabel id="site-select-label">Select Site</InputLabel>
                  <Select
                    labelId="site-select-label"
                    id="site-select"
                    value={selectedSite}
                    label="Select Site"
                    onChange={(e) => setSelectedSite(e.target.value)}
                    sx={{
                      borderRadius: "8px",
                      bgcolor: "#f8f9fa",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e3ec",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>All Sites</em>
                    </MenuItem>
                    {siteList.map((site) => (
                      <MenuItem key={site.id || site.site_id} value={site.id || site.site_id}>
                        {site.contact_name || site.contact_name || site.contact_name || `Site ${site.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Department Dropdown */}
              <Grid item xs={12} sm={4} md={4.5}>
                <FormControl fullWidth size="small">
                  <InputLabel id="department-select-label">Select Department</InputLabel>
                  <Select
                    labelId="department-select-label"
                    id="department-select"
                    value={selectedDepartment}
                    label="Select Department"
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    sx={{
                      borderRadius: "8px",
                      bgcolor: "#f8f9fa",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e3ec",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>All Departments</em>
                    </MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept.id || dept.department_id} value={dept.id || dept.department_id}>
                        {dept.department_name || dept.name || `Department ${dept.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>



            </Grid>
          </Paper>

          {/* Charts */}
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: "100%" }}>
                <AttendanceChart apiResponse={sampleData} />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ height: "100%" }}>
                <PunchLogsDashboard apiResponse={currentWorkingEmployees} />
              </Box>
            </Grid>
          </Grid>
        </Container>
        <AttendanceGraph />
      </Box>
    </>
  );
}

export default Dashboard;